// @ts-strict-ignore
import type { WorkflowState } from 'flow/store/redux/state';
import type { DerivedNodeConnectorState } from 'flow/store/redux/types';
import type { WorkflowEdge } from './edges';
import {
  buildEdge,
  buildEdges,
  buildSkeletonEdge,
  getEdgeId,
  isSourceSnippet,
  replaceNodesInEdges,
} from './edges';
import type {
  FolderNode,
  SnippetNode,
  WorkflowNode,
} from './nodes';
import {
  buildNodeFolder,
  buildNodes,
  buildSkeletonNode,
  filterNodes,
  FOLDER_NODE_ID,
  getLayoutContainerId,
  indexWorkflowNodes,
  isContainedBySnippet,
  isInputAttributeNode,
  isSnippetInternalChildNode,
  isSnippetNode,
} from './nodes';
import type { Graph } from './types';
import type { PublicId } from 'flow/store/engine/types';
import type {
  AdjacencyMap,
  BidirectionalGraphTopology,
  MaxPathLengthMap,
} from './layout';
import {
  getBidirectionalGraphTopology,
  getMaxPathLengthMap,
} from './layout';
import type { NodeDimensions } from './layout/nodeMatrix';
import { defaultNodeDimensions, getDimensions } from './layout/nodeMatrix';

export function buildGraph(
  actions: WorkflowState['workflowData']['actions'],
  dependencies: WorkflowState['workflowData']['dependencies'],
  workflowProps?: WorkflowState['workflowProps'],
): Graph {
  const nodes = buildNodes(actions, workflowProps);
  const edges = buildEdges(dependencies, nodes);

  const graph: Graph = {
    nodes,
    edges,
  };

  return graph;
}

/** Returns true if both source & target of the edge exists within the supplied nodes. */
function isEdgeInNodes(
  edge: WorkflowEdge,
  indexedNodes: Record<WorkflowNode['id'], WorkflowNode>,
) {
  const isTargetInSubgraph = Boolean(indexedNodes[edge.target]);
  const isSourceInSubgraph = Boolean(indexedNodes[edge.source]);

  return isTargetInSubgraph && isSourceInSubgraph;
}

export function filterSubgraph(
  graph: Graph,
  nodeTest: (node: WorkflowNode) => boolean,
): Graph {
  const filteredNodes = filterNodes(graph.nodes, nodeTest);
  const indexedNodes = indexWorkflowNodes(filteredNodes);

  const filteredEdges = graph.edges.filter(edge => (
    isEdgeInNodes(edge, indexedNodes)
  ));

  return {
    nodes: filteredNodes,
    edges: filteredEdges,
  };
}

/**
 * builds an index of snippet id -> internal child subgraphs
 * by filtering from the graph
*/
export function buildSnippetSubgraphs(graph: Graph) {
  const snippetSubGraphs: Record<PublicId, Graph> = {};

  graph.nodes.forEach(node => {
    if (isSnippetNode(node)) {
      const snippetSubGraph = filterSubgraph(
        graph,
        (testedNode: WorkflowNode) => isContainedBySnippet(node.id, testedNode),
      );
      snippetSubGraphs[node.id] = snippetSubGraph;
    }
  });

  return snippetSubGraphs;
}

export function buildSnippetSubgraphTopolgies(subGraphs: Record<PublicId, Graph>) {
  const snippetSubGraphTopoligies: Record<PublicId, BidirectionalGraphTopology> = {};

  Object.entries(subGraphs).forEach(([publicId, subGraph]) => {
    snippetSubGraphTopoligies[publicId] = getBidirectionalGraphTopology(
      subGraph, { snippets: {}, inputAttribute: { isCollapsed: false } },
    );
  });

  return snippetSubGraphTopoligies;
}

export function buildSnippetSubgraphIncomersMap(
  subGraphTopolgies: Record<PublicId, BidirectionalGraphTopology>,
) {
  const incomersMaps: Record<PublicId, AdjacencyMap> = {};

  Object.entries(subGraphTopolgies).forEach(([publicId, subGraphTopology]) => {
    incomersMaps[publicId] = subGraphTopology.incomers;
  });

  return incomersMaps;
}

export function buildSnippetSubgraphMaxPathMaps(
  subGraphIncomers: Record<PublicId, AdjacencyMap>,
  subGraphs: Record<PublicId, Graph>,
) {
  const maxLengthMaps: Record<PublicId, MaxPathLengthMap> = {};

  Object.entries(subGraphIncomers).forEach(([publicId, incomers]) => {
    const { nodes } = subGraphs[publicId];

    const maxPathMap = getMaxPathLengthMap({ incomers, nodes });

    maxLengthMaps[publicId] = maxPathMap;
  });

  return maxLengthMaps;
}

/**
 * Collapses Snippet Children within the dependency list.
 * This is used when computing the max path length for nodes that are dependent on the snippet.
 * Replacing Snippet children with the snippet node itself allows us to
 * drop in the dimensional width of the snippet, ensuring that all nodes
 * downstream of a snippet always render after the snippet.
 * This only used in node column placement.
 */
export function collapseSnippetChildIncomers(args: {
  graph: Graph;
  incomers: AdjacencyMap;
}): AdjacencyMap {
  const { graph, incomers } = args;
  const indexedNodes = indexWorkflowNodes(graph.nodes);
  const collapsed: AdjacencyMap = new Map<WorkflowNode['id'], Set<WorkflowNode['id']>>();

  incomers.forEach((sources, targetId) => {
    const transformedSources = new Set<WorkflowNode['id']>();
    const targetNode = indexedNodes[targetId];

    if (isSnippetInternalChildNode(targetNode)) {
      // skip internal snippet child nodes
      collapsed.set(targetId, sources);
      return;
    }

    sources.forEach(publicId => {
      const sourceNode = indexedNodes[publicId];

      if (isSnippetInternalChildNode(sourceNode)) {
        const parentId = getLayoutContainerId(sourceNode);
        transformedSources.add(parentId);
      } else {
        transformedSources.add(publicId);
      }
    });

    collapsed.set(targetId, transformedSources);
  });

  return collapsed;
}

export function buildSubgraphDimensions(
  subGraphMaxPathMaps: Record<PublicId, MaxPathLengthMap>,
  subgraphCollapsedState: WorkflowState['ui']['nodeContainers']['snippets'],
) {
  const dimensions: Record<PublicId, NodeDimensions> = {};

  Object.entries(subGraphMaxPathMaps).forEach(([publicId, maxPathMap]) => {
    if (subgraphCollapsedState[publicId].isCollapsed) {
      dimensions[publicId] = defaultNodeDimensions;
      return;
    }
    const snippetDimensions = getDimensions({ maxPathLengthMap: maxPathMap });

    dimensions[publicId] = snippetDimensions;
  });


  return dimensions;
}

/**
 * Returns the graph where a folder node is appended to the list of nodes.
 * Does not collapse folder children into the children.
 */
export function appendFolderNode(graph: Graph): Graph {
  const folderNode = buildNodeFolder();
  graph.nodes.push(folderNode);

  graph.nodes.forEach(node => {
    if (isInputAttributeNode(node)) {
      node.data.layout.containerId = folderNode.id;
    }
  });

  return graph;
}

/** Returns true if at least 1 snippet is collapsed, false otherwise */
function getShouldCollapseSnippets(
  snippetState: WorkflowState['ui']['nodeContainers']['snippets'],
) {
  const collapsedStates = Object.values(snippetState);

  for (const { isCollapsed } of collapsedStates) {
    if (isCollapsed) {
      return true;
    }
  }

  return false;
}

function isExternalToInternalSnippetChildEdge(
  edge: WorkflowEdge,
  indexedNodes: Record<WorkflowNode['id'], WorkflowNode>,
) {
  const targetNode = indexedNodes[edge.target];
  const sourceNode = indexedNodes[edge.source];

  return (
    isSnippetInternalChildNode(targetNode)
      && !isSourceSnippet(edge)
      && !isSnippetInternalChildNode(sourceNode)
  );
}

// A snippet's dependencies cana be considered to be the union of:
// - the dependencies that triggers the snippet parent
// - the dependencies external from the snippet that trigger its internal children
//
// to ensure a snippet's dependencies are accurate, add edges to the graph such that:
//  edges pointing from external nodes -> internal snippet child nodes,
//    instead point from external nodes -> snippet node
export function addExternalToInternalSnippetEdges(
  edges: Graph['edges'],
  indexedNodes: Record<WorkflowNode['id'], WorkflowNode>,
) {
  const discovered: Set<WorkflowEdge['id']> = new Set();

  return edges.flatMap(edge => {
    const targetNode = indexedNodes[edge.target];

    if (!isExternalToInternalSnippetChildEdge(edge, indexedNodes)) {
      return [edge];
    }

    const parentNode = indexedNodes[getLayoutContainerId(targetNode)];

    const newEdgeId = getEdgeId(edge.source, parentNode.id);

    if (discovered.has(newEdgeId)) {
      return [edge];
    }

    const newEdge = buildEdge({
      ...edge,
      id: newEdgeId,
      target: parentNode.id,
    });

    discovered.add(newEdgeId);

    return [
      edge,
      newEdge,
    ];
  });
}

/**
 * Builds and returns a new graph where container children are organized into their respective node containers.
 * Adjusts edges to point to the node containers where necessary.
 * Assumes that the container nodes already exist in the graph.
 */
export function organizeContainerNodes(
  graph: Graph,
  nodeContainerState: WorkflowState['ui']['nodeContainers'],
): Graph {
  const { inputAttribute: nodeFolderState, snippets: snippetCollapsedState } = nodeContainerState;

  //
  // STEP 1: TOP LEVEL NODE ARRAY ADJUSTMENTS
  // - removes collapsed nodes from both folders and snippets from the top level node array
  //  - add them to the inner node container data
  // - memoize id remappings to later perform edge adjustments
  //

  // memoized edge adjustments
  const collapsedEdgeIdMap: Record<WorkflowNode['id'], WorkflowNode['id']> = {};
  const indexedNodes = indexWorkflowNodes(graph.nodes);

  const folderNode = (
    indexedNodes[FOLDER_NODE_ID.inputAttribute] as FolderNode | undefined
  );

  // returns true if node is collapsed into folder, false otherwise
  function collapseIntoFolder(node: WorkflowNode) {
    if (!isInputAttributeNode(node)) return false;

    folderNode.data.children.push(node);
    collapsedEdgeIdMap[node.id] = folderNode.id;
    return true;
  }

  // returns true if node is collapsed into snippet, false otherwise
  function collapseIntoSnippets(node: WorkflowNode) {
    if (!isSnippetInternalChildNode(node)) return false;

    // check if parent container is collapsed
    const snippetNode = indexedNodes[getLayoutContainerId(node)] as SnippetNode;

    const { isCollapsed } = snippetCollapsedState[snippetNode.id];
    if (!isCollapsed) return false;

    snippetNode.data.children.push(node);
    collapsedEdgeIdMap[node.id] = snippetNode.id;
    return true;
  }

  const shouldCollapseFolder = folderNode && nodeFolderState.isCollapsed;
  const shouldCollapseSnippets = getShouldCollapseSnippets(snippetCollapsedState);

  const collapsers = [
    ...(shouldCollapseFolder ? [collapseIntoFolder] : []),
    ...(shouldCollapseSnippets ? [collapseIntoSnippets] : []),
  ];

  const nodes = graph.nodes.reduce((builtNodes, node) => {
    for (const collapse of collapsers) {
      // if the node was collapsed into a container,
      // do not add it to the graph's top level node array
      if (collapse(node)) return builtNodes;
    }
    return [...builtNodes, node];
  }, [] as WorkflowNode[]);

  //
  // STEP 2: EDGE ADJUSTMENTS
  // - remaps edges based on:
  //    - a nodes was collapsed and no longer exists within the node array

  // perform the id replacement for collapsed edges
  const edges = replaceNodesInEdges({
    edges: graph.edges,
    prevIdToNewIdMap: collapsedEdgeIdMap,
  });

  const snippetAdjustedEdges = addExternalToInternalSnippetEdges(edges, indexedNodes);

  return { nodes, edges: snippetAdjustedEdges };
}

/** Takes in a graph, and adjusts the edges to be render-friendly */
export function adjustEdgesForSnippetNodes(
  graph: Graph,
  snippetCollapsedStates: WorkflowState['ui']['nodeContainers']['snippets'],
) {
  const indexedNodes = indexWorkflowNodes(graph.nodes);

  // snippets that are expanded
  const expandedSnippetIds = new Set<WorkflowNode['id']>();
  const statesById = Object.entries(snippetCollapsedStates);
  for (const [snippetId, { isCollapsed }] of statesById) {
    if (!isCollapsed) expandedSnippetIds.add(snippetId);
  }

  return graph.edges.filter(edge => {
    // when snippet is expanded, remove edges whose source is that snippet
    if (expandedSnippetIds.has(edge.source)) return false;

    // remove external -> internal snippet child edges
    if (isExternalToInternalSnippetChildEdge(edge, indexedNodes)) return false;

    return true;
  });
}

export function appendAdditionalGraphElements(
  graph: Graph,
  uiStatePartial: {
    ownerNodeId: WorkflowState['ui']['connector']['ownerNodeId'];
  },
): Graph {
  const { ownerNodeId } = uiStatePartial;
  const skeletonNode = buildSkeletonNode();
  const skeletonEdge = buildSkeletonEdge(ownerNodeId);

  graph.nodes.push(skeletonNode);
  graph.edges.push(skeletonEdge);

  return graph;
}

export function shouldAddSkeletonNodeAndEdge(
  nodeConnectorState: DerivedNodeConnectorState,
  isEditingLinkedChildAction: boolean,
): boolean {
  const { intent, interactionState } = nodeConnectorState;
  return intent === 'add'
    && (interactionState === 'hovered') && !isEditingLinkedChildAction;
}

